export type TranslationKeys = {
  // Navigation
  navHome: string;
  navAllTools: string;
  navCategories: string;
  navAbout: string;
  navBlog: string;
  navContact: string;
  
  // Category Hubs
  hubPdf: string;
  hubImage: string;
  hubText: string;
  hubDev: string;
  hubCalc: string;
  hubFinance: string;
  hubColor: string;
  hubSecurity: string;
  hubSocial: string;
  hubFile: string;
  hubData: string;
  hubProductivity: string;
  hubUnit: string;
  hubTimeDate: string;
  hubMisc: string;

  // Category Keys
  'pdf-tools_title': string;
  'pdf-tools_desc': string;
  'image-tools_title': string;
  'image-tools_desc': string;
  'text-tools_title': string;
  'text-tools_desc': string;
  'developer-tools_title': string;
  'developer-tools_desc': string;
  'color-tools_title': string;
  'color-tools_desc': string;
  'security-tools_title': string;
  'security-tools_desc': string;
  'social-tools_title': string;
  'social-tools_desc': string;
  calculators_title: string;
  calculators_desc: string;
  'file-tools_title': string;
  'file-tools_desc': string;
  converters_title: string;
  converters_desc: string;
  'data-tools_title': string;
  'data-tools_desc': string;
  'productivity-tools_title': string;
  'productivity-tools_desc': string;
  'unit-conversion_title': string;
  'unit-conversion_desc': string;
  'time-date-tools_title': string;
  'time-date-tools_desc': string;
  'financial-tools_title': string;
  'financial-tools_desc': string;
  'misc-tools_title': string;
  'misc-tools_desc': string;

  // Hero Badges & Pill
  hundredPercentFree: string;
  noSignUp: string;
  worksOffline: string;
  badgeNoRegistration: string;
  badgeFreeForever: string;
  badgePrivacyFocused: string;
  badgeYourDataStays: string;
  badgeSuperFast: string;
  badgeInstantResults: string;

  // Header & Controls
  favorite: string;
  favorites: string;
  lightMode: string;
  darkMode: string;
  searchPlaceholder: string;
  selectLanguage: string;
  
  // Hero & Homepage
  heroBadge: string;
  heroTitleLine1: string;
  heroTitleLine2: string;
  heroDescription: string;
  exploreToolsBtn: string;
  browseCategoriesBtn: string;
  popularTools: string;
  allCategoriesTitle: string;
  chooseCategorySub: string;
  featuredToolsTitle: string;
  quickAccess: string;

  // All Tools & Sidebar
  allToolsSubtitle: string;
  popular: string;
  filterNew: string;
  noToolsFound: string;
  noToolsSub: string;
  resetFilters: string;
  bookmarkFavorites: string;
  bookmarkSubtitle: string;
  addFavorites: string;
  requestToolTitle: string;
  requestToolSubtitle: string;
  requestToolBtn: string;

  // Stats & Newsletter
  'tools-stat': string;
  'users-stat': string;
  'privacy-stat': string;
  'speed-stat': string;
  'offline-stat': string;
  newsletterTitle: string;
  newsletterSub: string;
  newsletterPlaceholder: string;
  newsletterSubscribe: string;
  newsletterSuccess: string;
  
  // Footer
  footerDescription: string;
  footerAboutSub: string;
  learnMore: string;
  quickLinks: string;
  toolCategories: string;
  legalPrivacy: string;
  legalTerms: string;
  allRightsReserved: string;
  
  // Modals & General
  close: string;
  cancel: string;
  copiedToClipboard: string;
  searchNoResults: string;
  launchTool: string;
  useTool: string;
};

export const TRANSLATIONS: Record<string, Partial<TranslationKeys>> = {
  en: {
    navHome: "Home",
    navAllTools: "All Tools",
    navCategories: "Categories",
    navAbout: "About",
    navBlog: "Blog",
    navContact: "Contact",

    hubPdf: "PDF Tools",
    hubImage: "Image Tools",
    hubText: "Text Tools",
    hubDev: "Developer Tools",
    hubCalc: "Calculators",
    hubFinance: "Finance Tools",
    hubColor: "Colour Tools",
    hubSecurity: "Security Tools",
    hubSocial: "Social Media Tools",
    hubFile: "File Tools",
    hubData: "Data Tools",
    hubProductivity: "Productivity",
    hubUnit: "Unit Converters",
    hubTimeDate: "Time & Date",
    hubMisc: "Misc Tools",

    favorite: "Favorite",
    favorites: "Favorites",
    lightMode: "Light Mode",
    darkMode: "Dark Mode",
    searchPlaceholder: "Search 450+ browser tools (e.g. PDF, JSON, Password)...",
    selectLanguage: "Select Language",

    heroBadge: "450+ Fast Browser Utilities",
    heroTitleLine1: "All-in-One Powerful",
    heroTitleLine2: "Free Online Tools",
    heroDescription: "Lightning-fast, client-side browser tools for developers, designers, writers, and power users. 100% private & secure.",
    exploreToolsBtn: "Explore All Tools",
    browseCategoriesBtn: "Browse Categories",
    popularTools: "Popular Tools",
    allCategoriesTitle: "Explore Our Tool Categories",
    chooseCategorySub: "Choose a category to explore our collection of free tools",
    featuredToolsTitle: "Featured Free Tools",
    quickAccess: "Quick Access",

    hundredPercentFree: "100% Free",
    noSignUp: "No Sign Up",
    worksOffline: "Works Offline",
    badgeNoRegistration: "No Registration",
    badgeFreeForever: "100% Free Forever",
    badgePrivacyFocused: "Privacy Focused",
    badgeYourDataStays: "Your data stays with you",
    badgeSuperFast: "Super Fast",
    badgeInstantResults: "Instant results",

    allToolsSubtitle: "Explore our collection of 450+ free online tools. Fast, secure and easy to use.",
    popular: "Popular",
    filterNew: "New",
    noToolsFound: "No tools found",
    noToolsSub: "Try clearing your search query or selecting a different category filter.",
    resetFilters: "Reset All Filters",
    bookmarkFavorites: "Bookmark Your Favorites",
    bookmarkSubtitle: "Save your favorite tools for quick access anytime.",
    addFavorites: "Add to Favorites",
    requestToolTitle: "Can't find the tool you need?",
    requestToolSubtitle: "We're adding new tools regularly. Request a tool and we'll build it for you.",
    requestToolBtn: "Request a Tool",

    'tools-stat': "Free Tools",
    'users-stat': "Happy Users",
    'privacy-stat': "Privacy First",
    'speed-stat': "Results",
    'offline-stat': "Offline",
    newsletterTitle: "Stay Updated",
    newsletterSub: "Get notified about new tools and updates",
    newsletterPlaceholder: "Enter your email",
    newsletterSubscribe: "Subscribe",
    newsletterSuccess: "Thank you for subscribing! We'll keep you updated.",

    'pdf-tools_title': "PDF Tools",
    'pdf-tools_desc': "Merge, split, compress, convert PDF files and more",
    'image-tools_title': "Image Tools",
    'image-tools_desc': "Edit, convert, compress images and apply effects",
    'text-tools_title': "Text Tools",
    'text-tools_desc': "Count, convert, format and manipulate text",
    'developer-tools_title': "Developer Tools",
    'developer-tools_desc': "Format, validate, convert and generate code",
    'color-tools_title': "Color Tools",
    'color-tools_desc': "Pick, convert, generate and explore colors",
    'security-tools_title': "Security Tools",
    'security-tools_desc': "Generate passwords, hashes and secure your data",
    'social-tools_title': "Social Media Tools",
    'social-tools_desc': "Optimize content for all social media platforms",
    calculators_title: "Calculators",
    calculators_desc: "Calculate anything with our smart calculators",
    'file-tools_title': "File Tools",
    'file-tools_desc': "Analyze, convert and manage your files",
    converters_title: "Converters",
    converters_desc: "Convert between different formats easily",
    'data-tools_title': "Data Tools",
    'data-tools_desc': "View, format, analyze and manipulate data",
    'productivity-tools_title': "Productivity Tools",
    'productivity-tools_desc': "Boost your productivity with useful tools",
    'unit-conversion_title': "Unit Conversion",
    'unit-conversion_desc': "Convert between units of measurement",
    'time-date-tools_title': "Time & Date Tools",
    'time-date-tools_desc': "Calculate time, dates and durations",
    'financial-tools_title': "Financial Tools",
    'financial-tools_desc': "Calculate loans, EMI, interest and more",
    'misc-tools_title': "Miscellaneous",
    'misc-tools_desc': "Random generators, utilities and fun tools",

    footerDescription: "Dualis Tools offers over 450+ free, instant, privacy-first web utilities designed for ultimate productivity.",
    footerAboutSub: "Dualis Tools is a free online platform providing 450+ tools to make your daily tasks easier.",
    learnMore: "Learn More",
    quickLinks: "Quick Links",
    toolCategories: "Tool Categories",
    legalPrivacy: "Privacy Policy",
    legalTerms: "Terms of Service",
    allRightsReserved: "All rights reserved.",

    close: "Close",
    cancel: "Cancel",
    copiedToClipboard: "Copied to clipboard!",
    searchNoResults: "No matching tools found.",
    launchTool: "Open Tool",
    useTool: "Use Utility"
  },











};
